import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  useQuery,
} from '@tanstack/react-query';
import { useUserCollectionStore } from '../../stores/useUserCollectionStore';
import { useAuthStore } from '../../stores/useAuthStore';
import {
  getFavorites,
  updateWardrobeItem,
  deleteWardrobeItem,
  enhanceWardrobeItem,
  addFavorite,
  removeFavorite,
  subscribe,
  unsubscribe,
  getDicts,
  addWardrobeItem,
  getUserCollection,
  getPlans,
  getPlanUsage,
  initiatePayment,
  getPaymentStatus,
  resetAccount,
  cancelSubscriptionAtPeriodEnd,
} from './api';
import type { ObrazGetCatalogInput } from './api.types';

export const useGetFavoritesInfinite = (
  params: Omit<ObrazGetCatalogInput, 'page'> = { take: 20 }
) => {
  return useInfiniteQuery({
    queryKey: ['catalog', 'favorites', params],
    queryFn: async ({ pageParam = 1 }) => {
      return getFavorites({ ...params, page: pageParam as number });
    },
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      if (lastPage.page < lastPage.pages) {
        return lastPage.page + 1;
      }
      return undefined;
    },
  });
};

export const useToggleFavoriteMutation = () => {
  const queryClient = useQueryClient();
  const addFavoriteToStore = useUserCollectionStore(s => s.addFavorite);
  const removeFavoriteFromStore = useUserCollectionStore(s => s.removeFavorite);

  return useMutation({
    mutationFn: async ({
      id,
      isFavorite,
    }: {
      id: string;
      isFavorite: boolean;
    }) => {
      if (isFavorite) {
        await addFavorite(id);
      } else {
        await removeFavorite(id);
      }
    },
    onSuccess: (_, { id, isFavorite }) => {
      if (isFavorite) {
        addFavoriteToStore(id);
      } else {
        removeFavoriteFromStore(id);
      }
      queryClient.invalidateQueries({ queryKey: ['catalog'] });
    },
  });
};

export const useToggleSubscriptionMutation = () => {
  const queryClient = useQueryClient();
  const addSubscriptionToStore = useUserCollectionStore(s => s.addSubscription);
  const removeSubscriptionFromStore = useUserCollectionStore(
    s => s.removeSubscription
  );

  return useMutation({
    mutationFn: async ({
      id,
      isSubscribed,
    }: {
      id: string;
      isSubscribed: boolean;
    }) => {
      if (isSubscribed) {
        await subscribe(id);
      } else {
        await unsubscribe(id);
      }
    },
    onSuccess: (_, { id, isSubscribed }) => {
      if (isSubscribed) {
        addSubscriptionToStore(id);
      } else {
        removeSubscriptionFromStore(id);
      }
      queryClient.invalidateQueries({ queryKey: ['catalog'] });
    },
  });
};

export const useDictionaries = () => {
  return useQuery({
    queryKey: ['dictionaries'],
    queryFn: getDicts,
    staleTime: Infinity,
  });
};

export const useAddWardrobeItemMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addWardrobeItem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wardrobe', 'items'] });
      queryClient.invalidateQueries({ queryKey: ['wardrobe', 'my-items'] });
    },
  });
};

export const useUpdateWardrobeItemMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateWardrobeItem,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['wardrobe', 'items'] });
      queryClient.invalidateQueries({ queryKey: ['wardrobe', 'my-items'] });
      queryClient.invalidateQueries({
        queryKey: ['wardrobe', 'item', variables.id],
      });
    },
  });
};

export const useDeleteWardrobeItemMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteWardrobeItem,
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['wardrobe', 'items'] });
      queryClient.invalidateQueries({ queryKey: ['wardrobe', 'my-items'] });
      queryClient.invalidateQueries({ queryKey: ['wardrobe', 'item', id] });
    },
  });
};

export const useEnhanceWardrobeItemMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: enhanceWardrobeItem,
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['wardrobe', 'items'] });
      queryClient.invalidateQueries({ queryKey: ['wardrobe', 'my-items'] });
      queryClient.invalidateQueries({ queryKey: ['wardrobe', 'item', id] });
    },
  });
};

type UseLoadUserCollectionOptions = {
  enabled?: boolean;
};

export const useLoadUserCollection = ({
  enabled = true,
}: UseLoadUserCollectionOptions = {}) => {
  const isAuth = useAuthStore(state => !!state.token);
  const setCollection = useUserCollectionStore(state => state.setCollection);

  return useQuery({
    queryKey: ['user', 'collection'],
    queryFn: async () => {
      const data = await getUserCollection();
      setCollection(data.favorites, data.subscriptions);
      return data;
    },
    enabled: enabled && isAuth,
  });
};

export const useGetPlansQuery = () => {
  const isAuth = useAuthStore(state => !!state.token);
  return useQuery({
    queryKey: ['plans'],
    queryFn: getPlans,
    enabled: isAuth,
    staleTime: 5 * 60 * 1000,
  });
};

export const useGetPlanUsageQuery = () => {
  const isAuth = useAuthStore(state => !!state.token);
  return useQuery({
    queryKey: ['plans', 'usage'],
    queryFn: getPlanUsage,
    enabled: isAuth,
    staleTime: 0,
  });
};

export const useResetAccountMutation = () => {
  return useMutation({
    mutationFn: resetAccount,
  });
};

export const useCancelSubscriptionAtPeriodEndMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: cancelSubscriptionAtPeriodEnd,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['plans', 'usage'] });
    },
  });
};

const PAYMENT_STATUS_POLL_INTERVAL_MS = 2000;

export const useInitiatePaymentMutation = () => {
  return useMutation({
    mutationFn: (planType: string) => initiatePayment(planType),
    onSuccess: ({ paymentId, confirmationUrl }) => {
      localStorage.setItem('pendingPaymentId', paymentId);
      window.location.href = confirmationUrl;
    },
  });
};

export const useGetPaymentStatusQuery = (paymentId: string | null) => {
  return useQuery({
    queryKey: ['payments', 'status', paymentId],
    queryFn: () => getPaymentStatus(paymentId!),
    enabled: !!paymentId,
    refetchInterval: query => {
      const status = query.state.data?.status;
      return status === 'pending' ? PAYMENT_STATUS_POLL_INTERVAL_MS : false;
    },
    staleTime: 0,
  });
};
